import { CardElement } from '@stripe/react-stripe-js';
import API from '../services/AxiosInstance';
import {
	Address,
	NewPaymentMethodDetails,
	PaymentIntentResponse,
	PaymentOption,
	RenewalsClient,
	SavedPaymentMethodDetails,
	TakeUpRenewalCommand,
	PaymentClient,
} from '../services/renewalsApi';
import { IRenewalMakePaymentForm } from '../pages/renewal/MakePayment';
import { FullPolicy } from '../services/api';
import { ICreateNewRenewalVersionResponse } from '../store/renewal.slice';
import { Stripe, StripeElements } from '@stripe/stripe-js';

interface IProps {
	policy: FullPolicy;
	renewal: ICreateNewRenewalVersionResponse;
	stripe: Stripe | null;
	elements: StripeElements | null;
}

const useStripeRenewalPayment = ({ policy, renewal, stripe, elements }: IProps) => {
	const getPaymentIntent = async (amount: number, saveCard: boolean, billingAddress: Address) => {
		const client = new PaymentClient(undefined, API);
		try {
			return await client.makePaymentIntent({
				policyToken: policy.policyToken,
				saveCard,
				currencyCode: 'GBP',
				amount,
				billingAddress,
				product: policy.product,
			});
		} catch (error) {
			console.error('Error creating payment intent:', error);
			throw new Error('Failed to create payment intent');
		}
	};

	const confirmPayment = async (
		values: IRenewalMakePaymentForm,
		paymentIntent: PaymentIntentResponse,
		useExistingCard?: boolean,
	) => {
		if (!stripe || !elements) {
			throw new Error('Stripe or elements not initialized');
		}

		const cardElement = elements.getElement(CardElement);
		if (!cardElement) {
			throw new Error('Card element not found');
		}

		try {
			const billingDetails = useExistingCard
				? {
						name: renewal.drivers.find((driver) => driver.relationshipToProposer === 'P')?.firstName,
						address: renewal.policyDetails.policyholder.address,
					}
				: {
						name: `${values.cardholderFirstName} ${values.cardholderLastName}`,
						email: values.cardholderEmail,
						address: {
							line1: values.address.line1,
							line2: values.address.line2,
							postal_code: values.address.postcode,
							city: values.address.city,
						},
					};

			return await stripe.confirmCardPayment(paymentIntent.paymentId, {
				payment_method: {
					card: cardElement,
					billing_details: billingDetails,
				},
			});
		} catch (error) {
			console.error('Error confirming payment:', error);
			throw new Error('Failed to confirm payment');
		}
	};

	const takeUpRenewal = async (
		paymentOption: PaymentOption,
		isFutureStartDate: boolean,
		sendPostalDocuments: boolean,
		startDate?: string,
		authorizationId?: string,
		paymentId?: string,
		balanceSettlementDetails?: SavedPaymentMethodDetails,
		newBalanceSettlementDetails?: NewPaymentMethodDetails,
		existingCardPaymentMethodId?: string,
	) => {
		if (!renewal.renewalRatingResponse?.underwrittenOptionId) {
			throw new Error('No rate');
		}

		const client = new RenewalsClient(undefined, API);
		const transactionId = renewal.newRenewalTransactionId || (policy.renewalDetails?.transactionId as string);

		const takeUpCommand: TakeUpRenewalCommand = {
			policyToken: policy.policyToken,
			transactionId,
			underwrittenOptionId: renewal.renewalRatingResponse.underwrittenOptionId,
			paymentOption,
			isFutureStartDate,
			startDate,
			...(authorizationId &&
				paymentId && {
					upFrontSettlementDetails: { paymentId, authorizationId },
				}),
			...(paymentOption !== PaymentOption.PayInFull &&
				balanceSettlementDetails && {
					savedBalanceSettlementDetails: balanceSettlementDetails,
				}),
			...(newBalanceSettlementDetails && {
				newBalanceSettlementDetails,
			}),
			...(existingCardPaymentMethodId && {
				existingCardPaymentMethodId,
			}),
			sendPostalDocuments,
		};

		try {
			return await client.takeUp(takeUpCommand);
		} catch (error) {
			console.error('Error taking up renewal:', error);
			throw new Error('Failed to take up renewal');
		}
	};

	return {
		getPaymentIntent,
		confirmPayment,
		takeUpRenewal,
	};
};

export default useStripeRenewalPayment;
